import { useEffect, useState } from "react";
import { Badge, Button, Dropdown, message, Modal } from "antd";
import { CalendarOutlined, DownOutlined } from "@ant-design/icons";
import { toLocalDateString } from "../../utils/helpers";
import JDatePicker from "../DatePicker";
import ModalTitle from "../ModalTitle";
import styles from "./styles.module.css";

export const DATE_RANGE_PRESETS = [
  { key: "7", label: "۷ روز اخیر", days: 7 },
  { key: "30", label: "۳۰ روز اخیر", days: 30 },
  { key: "90", label: "۹۰ روز اخیر", days: 90 },
];

export const getPresetDateRange = (days = 7) => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - (days - 1));

  return {
    from: toLocalDateString(from),
    to: toLocalDateString(to),
  };
};

export const ALL_TIME_PRESET = { key: "all", label: "کل پروژه", days: null };
export const CUSTOM_DATE_PRESET = { key: "custom", label: "بازه دلخواه", days: null };
export const MAX_CUSTOM_RANGE_DAYS = 366;

const formatRangeDate = (date) =>
  new Date(`${date}T00:00:00`).toLocaleDateString("fa-IR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

const getSelectedPreset = (value, includeAll) => {
  if (!value?.from || !value?.to) {
    return includeAll ? ALL_TIME_PRESET : DATE_RANGE_PRESETS[0];
  }

  return DATE_RANGE_PRESETS.find((preset) => {
    const range = getPresetDateRange(preset.days);
    return range.from === value.from && range.to === value.to;
  });
};

const PresetDateRange = ({
  value,
  onChange,
  prefix = "بازه گزارش",
  includeAll = false,
}) => {
  const [customOpen, setCustomOpen] = useState(false);
  const [customRange, setCustomRange] = useState(value || {});
  const selectedPreset = getSelectedPreset(value, includeAll);
  const presets = includeAll
    ? [...DATE_RANGE_PRESETS, ALL_TIME_PRESET, CUSTOM_DATE_PRESET]
    : [...DATE_RANGE_PRESETS, CUSTOM_DATE_PRESET];
  const displayLabel = selectedPreset?.label ||
    (value?.from && value?.to
      ? `${formatRangeDate(value.from)} تا ${formatRangeDate(value.to)}`
      : CUSTOM_DATE_PRESET.label);

  useEffect(() => {
    if (customOpen) setCustomRange(value || {});
  }, [customOpen, value]);

  const applyCustomRange = () => {
    if (!customRange.from || !customRange.to) {
      message.error("تاریخ شروع و پایان را انتخاب کنید");
      return;
    }
    if (new Date(customRange.from) > new Date(customRange.to)) {
      message.error("تاریخ پایان باید بعد از تاریخ شروع باشد");
      return;
    }
    const rangeDays = Math.round(
      (new Date(`${customRange.to}T00:00:00`) - new Date(`${customRange.from}T00:00:00`)) / 86400000,
    ) + 1;
    if (rangeDays > MAX_CUSTOM_RANGE_DAYS) {
      message.error("بازه دلخواه نمی‌تواند بیشتر از یک سال باشد");
      return;
    }
    onChange?.({ ...customRange, preset: CUSTOM_DATE_PRESET });
    setCustomOpen(false);
  };

  return (
    <>
    <Dropdown
      trigger={["click"]}
      menu={{
        items: presets.map((preset) => ({
          key: preset.key,
          label: preset.label,
        })),
        selectable: true,
        selectedKeys: selectedPreset ? [selectedPreset.key] : value?.from && value?.to ? [CUSTOM_DATE_PRESET.key] : [],
        onClick: ({ key }) => {
          const preset = presets.find((item) => item.key === key);
          if (key === CUSTOM_DATE_PRESET.key) {
            setCustomOpen(true);
            return;
          }
          onChange?.(
            preset.days
              ? { ...getPresetDateRange(preset.days), preset }
              : { from: undefined, to: undefined, preset },
          );
        },
      }}
    >
      <Button
        type="text"
        size="small"
        aria-label={`تغییر بازه؛ بازه فعلی ${displayLabel}`}
        style={{
          paddingInline: 4,
          color: "var(--ds-text-secondary)",
          background: "transparent",
          border: 0,
          boxShadow: "none",
          whiteSpace: "nowrap",
        }}
      >
        <Badge
          status="processing"
          text={
            <span style={{ fontSize: 12, color: "var(--ds-text-secondary)" }}>
              {prefix}: {displayLabel}
            </span>
          }
        />
        <DownOutlined style={{ marginInlineStart: 6, fontSize: 10 }} />
      </Button>
    </Dropdown>
    <Modal
      open={customOpen}
      title={<ModalTitle icon={<CalendarOutlined />} tone="blue" title="انتخاب بازه دلخواه" subtitle="تاریخ شروع و پایان گزارش را مشخص کنید" />}
      className="ds-modal"
      centered
      okText="اعمال بازه"
      cancelText="انصراف"
      onOk={applyCustomRange}
      onCancel={() => setCustomOpen(false)}
      destroyOnClose
    >
      <div className={styles.customFields}>
        <label><span>از تاریخ</span><JDatePicker fullWidth size="large" value={customRange.from} onChange={(from) => setCustomRange((current) => ({ ...current, from: from?.split("T")[0] }))} /></label>
        <label><span>تا تاریخ</span><JDatePicker fullWidth size="large" value={customRange.to} onChange={(to) => setCustomRange((current) => ({ ...current, to: to?.split("T")[0] }))} /></label>
      </div>
    </Modal>
    </>
  );
};

export default PresetDateRange;
